import React, { useCallback, useEffect, useState } from "react";
import { Upload, Ticket, Receipt as ReceiptIcon, Coins, Check, Gift, Lock, Users, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

const TIER_RANK = { bronze: 0, silver: 1, gold: 2 };
const VOUCHER_FALLBACK = "https://images.unsplash.com/photo-1607013251379-e6eecfffe234?auto=format&fit=crop&w=800&q=80";

const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
};

const StatusBadge = ({ status }) => {
  const { t } = useLang();
  const map = {
    pending: "bg-brand-yellow text-brand-ink",
    approved: "bg-brand-green text-brand-ink",
    rejected: "bg-red-500 text-white",
  };
  return <span className={`rounded-full border-2 border-brand-ink px-3 py-0.5 text-xs font-bold ${map[status]}`}>{t(status)}</span>;
};

export const MemberDashboard = () => {
  const { user, refreshUser } = useAuth();
  const { t, pick } = useLang();
  const [tab, setTab] = useState("upload");
  const [amount, setAmount] = useState("");
  const [file, setFile] = useState(null);
  const [store, setStore] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [myVouchers, setMyVouchers] = useState([]);

  const loadAll = useCallback(() => {
    api.get("/my/receipts").then((r) => setReceipts(r.data)).catch(() => {});
    api.get("/vouchers").then((r) => setVouchers(r.data)).catch(() => {});
    api.get("/my/vouchers").then((r) => setMyVouchers(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const submitReceipt = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please attach a receipt photo.");
    setBusy(true);
    try {
      const path = await uploadFile(file);
      await api.post("/receipts", { image_path: path, amount: parseFloat(amount), store });
      toast.success("Receipt submitted! Points after approval.");
      setAmount(""); setFile(null); setStore("");
      loadAll();
    } catch (err) {
      toast.error("Upload failed. Try again.");
    } finally { setBusy(false); }
  };

  const redeem = async (v) => {
    try {
      await api.post(`/vouchers/${v.id}/redeem`);
      toast.success(`Redeemed: ${v.title}`);
      await refreshUser();
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cannot redeem");
    }
  };

  const handleUseVoucher = async (mv) => {
    try {
      await api.post(`/my/vouchers/${mv.id}/use`);
      toast.success("Voucher used!");
      loadAll();
    } catch (err) { toast.error("Failed"); }
  };

  const [birthday, setBirthday] = useState(user?.birthday || "");
  const [qrVoucher, setQrVoucher] = useState(null);
  const saveBirthday = async () => {
    try {
      await api.put("/me/profile", { birthday });
      await refreshUser();
      toast.success("Birthday saved! 🎂");
    } catch (err) { toast.error("Failed to save"); }
  };

  const copyReferral = async () => {
    const link = `${window.location.origin}/register?ref=${user?.referral_code || ""}`;
    try { await navigator.clipboard.writeText(link); toast.success("Referral link copied!"); }
    catch { toast.success(link); }
  };

  const tabs = [
    { id: "upload", label: t("upload_receipt"), Icon: Upload },
    { id: "receipts", label: t("my_receipts"), Icon: ReceiptIcon },
    { id: "store", label: t("rewards_store"), Icon: Coins },
    { id: "vouchers", label: t("my_vouchers"), Icon: Ticket },
    { id: "profile", label: t("profile"), Icon: Gift },
  ];

  return (
    <div className="mx-auto max-w-6xl px-5 py-10">
      {/* Points + tier header */}
      <div className="rounded-3xl border-2 border-brand-ink bg-brand-green p-7 shadow-brutal-lg">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <p className="text-sm font-bold uppercase tracking-widest">{user?.name}</p>
            <div className="mt-1 flex items-center gap-3">
              <p className="font-display text-2xl font-bold">{t("my_points")}</p>
              <span data-testid="member-tier" className="inline-flex items-center gap-1 rounded-full border-2 border-brand-ink bg-brand-yellow px-3 py-0.5 text-xs font-bold uppercase">
                {user?.tier || "Bronze"} {user?.tier_multiplier > 1 ? `· +${Math.round((user.tier_multiplier - 1) * 100)}% ${t("tier_bonus")}` : ""}
              </span>
            </div>
          </div>
          <div className="flex items-baseline gap-2" data-testid="member-points">
            <span className="font-display text-6xl font-black">{user?.points ?? 0}</span>
            <span className="font-bold">{t("points")}</span>
          </div>
        </div>
        {user?.next_tier ? (
          <div className="mt-5" data-testid="tier-progress">
            <div className="mb-1 flex justify-between text-xs font-bold">
              <span>{t("lifetime")}: {user?.lifetime_points ?? 0}</span>
              <span>{Math.max(0, (user.next_tier_at || 0) - (user.lifetime_points || 0))} {t("pts_to")} {user.next_tier}</span>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full border-2 border-brand-ink bg-white">
              <div className="h-full bg-brand-ink" style={{ width: `${Math.min(100, Math.round(((user.lifetime_points || 0) / (user.next_tier_at || 1)) * 100))}%` }} />
            </div>
          </div>
        ) : (
          <div className="mt-5 flex items-center justify-between text-xs font-bold" data-testid="tier-progress">
            <span>{t("lifetime")}: {user?.lifetime_points ?? 0}</span>
            <span className="rounded-full border-2 border-brand-ink bg-brand-yellow px-3 py-0.5 uppercase">{t("max_tier")}</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-6 flex flex-wrap gap-2">
        {tabs.map(({ id, label, Icon }) => (
          <button key={id} data-testid={`member-tab-${id}`} onClick={() => setTab(id)}
            className={`inline-flex items-center gap-2 rounded-full border-2 border-brand-ink px-4 py-2 text-sm font-bold transition-transform ${tab === id ? "bg-brand-ink text-brand-yellow" : "bg-white hover:-translate-y-0.5"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "upload" && (
          <form onSubmit={submitReceipt} className="max-w-lg space-y-4 rounded-3xl border-2 border-brand-ink bg-white p-7 shadow-brutal" data-testid="receipt-form">
            <p className="text-sm text-muted-foreground">{t("receipt_hint")}</p>
            <div>
              <label className="text-sm font-semibold">{t("amount_rm")}</label>
              <input data-testid="receipt-amount" type="number" step="0.01" min="0" required value={amount} onChange={(e) => setAmount(e.target.value)}
                className="mt-1 w-full rounded-xl border-2 border-brand-ink bg-brand-cream px-4 py-3 outline-none focus:ring-2 focus:ring-brand-green" />
            </div>
            <div>
              <label className="text-sm font-semibold">Store (optional)</label>
              <input value={store} onChange={(e) => setStore(e.target.value)}
                className="mt-1 w-full rounded-xl border-2 border-brand-ink bg-brand-cream px-4 py-3 outline-none focus:ring-2 focus:ring-brand-green" />
            </div>
            <div>
              <label className="text-sm font-semibold">Receipt Photo</label>
              <input data-testid="receipt-file" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])}
                className="mt-1 w-full rounded-xl border-2 border-dashed border-brand-ink bg-brand-cream px-4 py-3 text-sm" />
            </div>
            <button type="submit" disabled={busy} data-testid="receipt-submit" className="w-full rounded-full border-2 border-brand-ink bg-brand-green py-3 font-bold hover-brutal disabled:opacity-60">
              {busy ? "Uploading…" : t("upload_receipt")}
            </button>
          </form>
        )}

        {tab === "receipts" && (
          <div className="space-y-3" data-testid="my-receipts">
            {receipts.length === 0 && <p className="text-muted-foreground">No receipts yet.</p>}
            {receipts.map((r) => (
              <div key={r.id} className="flex items-center gap-4 rounded-2xl border-2 border-brand-ink bg-white p-4">
                <img src={fileUrl(r.image_path)} alt="receipt" onError={(e) => { e.currentTarget.src = "/panda-mascot.png"; }} className="h-16 w-16 rounded-lg border-2 border-brand-ink object-cover" />
                <div className="flex-1">
                  <p className="font-display text-xl font-bold">RM {r.amount?.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <StatusBadge status={r.status} />
                  {r.status === "approved" && <p className="mt-1 text-sm font-bold text-brand-greendark">+{r.points_awarded} pts</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "store" && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="rewards-store">
            {vouchers.length === 0 && <p className="text-muted-foreground">No rewards yet.</p>}
            {vouchers.map((v) => {
              const minTier = (v.min_tier || "bronze");
              const tierLocked = (TIER_RANK[user?.tier_key] ?? 0) < (TIER_RANK[minTier] ?? 0);
              const soldOut = v.stock != null && v.stock <= 0;
              const noPoints = (user?.points ?? 0) < v.points_cost;
              return (
                <div key={v.id} className={`flex flex-col overflow-hidden rounded-3xl border-2 border-brand-ink bg-white ${(tierLocked || soldOut) ? "opacity-70" : "hover-brutal"}`}>
                  <div className="relative">
                    <img src={v.image_path ? fileUrl(v.image_path) : VOUCHER_FALLBACK} alt={v.title} className="aspect-[4/3] w-full object-cover" />
                    {minTier !== "bronze" && (
                      <span className={`absolute left-3 top-3 inline-flex items-center gap-1 rounded-full border-2 border-brand-ink px-3 py-0.5 text-xs font-bold uppercase ${minTier === "gold" ? "bg-brand-yellow" : "bg-brand-green"}`}>
                        {tierLocked && <Lock className="h-3 w-3" />} {minTier} {t("only")}
                      </span>
                    )}
                    {v.stock != null && (
                      <span className={`absolute right-3 top-3 rounded-full border-2 border-brand-ink px-3 py-0.5 text-xs font-bold uppercase ${soldOut ? "bg-red-500 text-white" : "bg-white"}`}>
                        {soldOut ? t("sold_out") : `${v.stock} ${t("left")}`}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-display text-xl font-bold">{v.title}</h3>
                    <p className="mt-1 flex-1 text-sm text-muted-foreground">{v.description}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="rounded-full border-2 border-brand-ink bg-brand-yellow px-3 py-1 text-sm font-bold">{v.points_cost} pts</span>
                      <button onClick={() => redeem(v)} disabled={tierLocked || noPoints || soldOut} data-testid={`redeem-${v.id}`}
                        className={`inline-flex items-center gap-1 rounded-full border-2 border-brand-ink px-4 py-1.5 text-sm font-bold capitalize hover-brutal disabled:opacity-60 ${soldOut ? "bg-red-500 text-white" : "bg-brand-green"}`}>
                        {soldOut ? t("sold_out") : tierLocked ? <><Lock className="h-3 w-3" />{minTier} {t("only")}</> : t("redeem")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "vouchers" && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="my-vouchers">
            {myVouchers.length === 0 && <p className="text-muted-foreground">No vouchers yet. Redeem from the store!</p>}
            {myVouchers.map((mv) => (
              <div key={mv.id} className={`rounded-3xl border-2 border-brand-ink p-5 ${mv.status === "used" ? "bg-muted opacity-70" : "bg-white shadow-brutal"}`}>
                <div className="flex items-start justify-between">
                  <Ticket className="h-6 w-6 text-brand-greendark" />
                  {mv.status === "used"
                    ? <span className="inline-flex items-center gap-1 text-xs font-bold text-muted-foreground"><Check className="h-3 w-3" />{t("used")}</span>
                    : <span className="rounded-full border-2 border-brand-ink bg-brand-green px-2 py-0.5 text-xs font-bold">{t("active_v")}</span>}
                </div>
                <h3 className="mt-3 font-display text-xl font-bold">{mv.title}</h3>
                <p className="mt-1 font-mono text-sm tracking-widest">{mv.code}</p>
                <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                  <p>{t("redeemed_on")}: {new Date(mv.redeemed_at).toLocaleDateString()}</p>
                  {mv.used_at && <p>{t("used_on")}: {new Date(mv.used_at).toLocaleDateString()}</p>}
                  <p>{mv.points_cost} pts</p>
                </div>
                <button onClick={() => setQrVoucher(mv)} data-testid={`show-qr-${mv.id}`} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-brand-ink bg-white py-2 text-sm font-bold hover-brutal">
                  <QrCode className="h-4 w-4" /> {t("show_qr")}
                </button>
                {mv.status !== "used" && (
                  <button onClick={() => handleUseVoucher(mv)} data-testid={`use-voucher-${mv.id}`} className="mt-2 w-full rounded-full border-2 border-brand-ink bg-brand-green py-2 text-sm font-bold hover-brutal">
                    {t("use_now")}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "profile" && (
          <div className="grid max-w-3xl gap-6 md:grid-cols-2">
            <div className="rounded-3xl border-2 border-brand-ink bg-white p-7 shadow-brutal" data-testid="profile-panel">
              <div className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-brand-greendark" />
                <h3 className="font-display text-xl font-bold">{t("birthday_reward")}</h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{t("birthday_hint")}</p>
              <label className="mt-4 block text-sm font-semibold">{t("birthday")}</label>
              <input data-testid="birthday-input" type="date" value={birthday ? `2000-${birthday}` : ""} onChange={(e) => setBirthday(e.target.value.slice(5))}
                className="mt-1 w-full rounded-xl border-2 border-brand-ink bg-brand-cream px-4 py-3 outline-none focus:ring-2 focus:ring-brand-green" />
              {birthday && <p className="mt-2 text-sm font-bold text-brand-greendark">{t("your_birthday")}: {birthday}</p>}
              <button onClick={saveBirthday} data-testid="birthday-save" className="mt-4 w-full rounded-full border-2 border-brand-ink bg-brand-green py-3 font-bold hover-brutal">
                {t("save")}
              </button>
            </div>

            <div className="rounded-3xl border-2 border-brand-ink bg-brand-yellow p-7 shadow-brutal" data-testid="referral-panel">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                <h3 className="font-display text-xl font-bold">{t("refer_friends")}</h3>
              </div>
              <p className="mt-2 text-sm text-brand-ink/80">{t("refer_hint")}</p>
              <p className="mt-4 text-xs font-bold uppercase tracking-widest">{t("your_code")}</p>
              <div className="mt-1 flex items-center gap-2">
                <span data-testid="referral-code" className="rounded-xl border-2 border-brand-ink bg-white px-4 py-2 font-mono text-lg font-bold tracking-widest">{user?.referral_code || "—"}</span>
                <button onClick={copyReferral} data-testid="referral-copy" className="rounded-full border-2 border-brand-ink bg-brand-ink px-4 py-2 text-sm font-bold text-brand-yellow hover-brutal">{t("copy_link")}</button>
              </div>
              <p className="mt-5 font-display text-4xl font-black" data-testid="referral-count">{user?.referral_count ?? 0}</p>
              <p className="text-sm font-bold">{t("friends_joined")}</p>
            </div>
          </div>
        )}
      </div>

      {qrVoucher && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/60 p-5" onClick={() => setQrVoucher(null)} data-testid="qr-modal">
          <div className="w-full max-w-xs rounded-3xl border-2 border-brand-ink bg-white p-6 text-center shadow-brutal-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl font-bold">{qrVoucher.title}</h3>
            <p className="mt-1 text-xs text-muted-foreground">{t("qr_hint")}</p>
            <div className="mx-auto mt-4 w-fit rounded-2xl border-2 border-brand-ink bg-white p-3">
              <QRCodeSVG value={`${window.location.origin}/verify/${qrVoucher.code}`} size={180} fgColor="#0A0A0A" level="M" />
            </div>
            <p className="mt-3 font-mono text-lg font-bold tracking-widest">{qrVoucher.code}</p>
            <button onClick={() => setQrVoucher(null)} data-testid="qr-close" className="mt-4 w-full rounded-full border-2 border-brand-ink bg-brand-yellow py-2 text-sm font-bold hover-brutal">{t("close")}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberDashboard;
